import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";

/**
 * Small button that calls the AI assistant and passes back the result.
 *
 * Props:
 *  - task: "summary" | "titles" | "seo" | "tags"
 *  - label: text shown in the button
 *  - getPayload: () => ({title, summary, content, tags, category})
 *  - onResult: (result) => void  — receives the AI JSON payload
 *  - testId
 *  - size, variant, className: forwarded to Button
 */
export default function AiAssistButton({
  task,
  label,
  getPayload,
  onResult,
  testId,
  size = "sm",
  variant = "outline",
  className = "",
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (loading || disabled) return;
    const payload = { task, ...(getPayload?.() || {}) };
    if (!payload.content && !payload.title) {
      toast.error("Escribí el título o algo del cuerpo antes de usar la IA.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.adminAiAssist(payload);
      onResult?.(res.result);
      toast.success("Sugerencia generada");
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const detail = e?.response?.data?.detail;
      let msg;
      if (isTimeout) {
        msg = "La IA tardó demasiado. Volvé a intentar (a veces Claude está saturado).";
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        msg = detail.message || "Error del asistente IA";
      } else {
        msg = "No se pudo generar la sugerencia";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={run}
      variant={variant}
      size={size}
      disabled={loading || disabled}
      data-testid={testId}
      className={`gap-1.5 ${className}`}
      title="Generar con IA (Claude Sonnet 4.5)"
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      {label}
    </Button>
  );
}
